/**
 * Transient OHT pose overrides while riding a circle-junction turntable or
 * H-connector during backend playback. Kept out of Redux so RAF ticks stay light.
 */

export type RailTransferPose = {
  x: number;
  y: number;
  angle: number;
  /** Source of the override — useful for debugging / layering. */
  kind: 'turntable' | 'connector';
};

type Listener = () => void;

class RailTransferPoseStore {
  private poses = new Map<string, RailTransferPose>();
  private listeners = new Set<Listener>();

  get(ohtId: string): RailTransferPose | undefined {
    return this.poses.get(ohtId);
  }

  getAll(): ReadonlyMap<string, RailTransferPose> {
    return this.poses;
  }

  set(ohtId: string, pose: RailTransferPose) {
    const prev = this.poses.get(ohtId);
    if (
      prev &&
      prev.kind === pose.kind &&
      Math.abs(prev.x - pose.x) < 1e-6 &&
      Math.abs(prev.y - pose.y) < 1e-6 &&
      Math.abs(prev.angle - pose.angle) < 1e-6
    ) {
      return;
    }
    this.poses.set(ohtId, pose);
    this.emit();
  }

  clear(ohtId: string) {
    if (!this.poses.delete(ohtId)) return;
    this.emit();
  }

  /** Replace the whole map (one emit). Missing ids are cleared. */
  replaceAll(next: Map<string, RailTransferPose>) {
    let changed = this.poses.size !== next.size;
    if (!changed) {
      for (const [id, pose] of next) {
        const prev = this.poses.get(id);
        if (
          !prev ||
          prev.kind !== pose.kind ||
          Math.abs(prev.x - pose.x) >= 1e-6 ||
          Math.abs(prev.y - pose.y) >= 1e-6 ||
          Math.abs(prev.angle - pose.angle) >= 1e-6
        ) {
          changed = true;
          break;
        }
      }
    }
    this.poses = new Map(next);
    if (changed) this.emit();
  }

  reset() {
    if (this.poses.size === 0) return;
    this.poses.clear();
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const listener of this.listeners) listener();
  }
}

export const railTransferPoseStore = new RailTransferPoseStore();
